import { useEffect, useState } from 'react';
import type { UserAccount } from '../lib/userStore';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import { LOCALES, LOCALE_LABEL, type Locale } from '../i18n/types';
import styles from '../App.module.css';

interface ProfileSettingsProps {
  open: boolean;
  account: UserAccount | null;
  accounts: UserAccount[];
  suggestedDueDate: string;
  onSaveDueDate: (value: string) => void;
  onSaveBirthDate: (value: string) => void;
  onClear: () => void;
  onRename: (name: string) => void;
  onSwitch: (id: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onLocaleChange: (locale: Locale) => void;
  onCloudSave: () => Promise<{ ok: boolean; code?: string; message: string }>;
  onCloudRestore: (code: string) => Promise<{ ok: boolean; message: string }>;
  onClose: () => void;
}

export function ProfileSettings({
  open,
  account,
  accounts,
  suggestedDueDate,
  onSaveDueDate,
  onSaveBirthDate,
  onClear,
  onRename,
  onSwitch,
  onAdd,
  onDelete,
  onLocaleChange,
  onCloudSave,
  onCloudRestore,
  onClose,
}: ProfileSettingsProps) {
  const { locale, setLocale } = useLanguage();
  const ui = getUi(locale);
  const [mode, setMode] = useState<'pregnant' | 'postpartum'>(
    account?.birthDate ? 'postpartum' : 'pregnant'
  );
  const [dueDate, setDueDate] = useState(account?.dueDate ?? suggestedDueDate);
  const [birthDate, setBirthDate] = useState(account?.birthDate ?? '');
  const [name, setName] = useState(account?.name ?? '');
  const [status, setStatus] = useState('');
  const [restoreCode, setRestoreCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !account) return;
    setMode(account.birthDate ? 'postpartum' : 'pregnant');
    setDueDate(account.dueDate ?? suggestedDueDate);
    setBirthDate(account.birthDate ?? '');
    setName(account.name);
    setStatus('');
  }, [open, account, suggestedDueDate]);

  if (!open) return null;

  return (
    <div className={styles.onboardingOverlay} role="dialog" aria-modal="true" aria-labelledby="settings-title">
      <div className={styles.onboardingCard}>
        <h2 id="settings-title" className={styles.onboardingTitle}>
          {ui.settings.title}
        </h2>

        <div className={styles.onboardingField}>
          <span>{ui.settings.accountsLabel}</span>
          <div className={styles.accountList}>
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`${styles.accountChip} ${account?.id === a.id ? styles.accountChipActive : ''}`}
                onClick={() => {
                  onSwitch(a.id);
                  setLocale(a.locale);
                }}
              >
                {a.name}
              </button>
            ))}
          </div>
          <div className={styles.shareActions}>
            <button type="button" className={styles.ghostButton} onClick={onAdd}>
              {ui.settings.addAccount}
            </button>
            {account && accounts.length > 1 ? (
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => {
                  if (window.confirm(ui.settings.deleteConfirm.replace('{name}', account.name))) {
                    onDelete(account.id);
                  }
                }}
              >
                {ui.settings.deleteAccount}
              </button>
            ) : null}
          </div>
        </div>

        <label className={styles.onboardingField}>
          <span>{ui.onboarding.nameLabel}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.onboardingInput}
          />
        </label>

        <div className={styles.onboardingField}>
          <span>{ui.onboarding.languageLabel}</span>
          <div className={styles.langGrid}>
            {LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                className={`${styles.langChip} ${locale === code ? styles.langChipActive : ''}`}
                onClick={() => {
                  setLocale(code);
                  onLocaleChange(code);
                }}
              >
                {LOCALE_LABEL[code]}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.onboardingTabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'pregnant'}
            className={`${styles.onboardingTab} ${mode === 'pregnant' ? styles.onboardingTabActive : ''}`}
            onClick={() => setMode('pregnant')}
          >
            {ui.onboarding.pregnantTab}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'postpartum'}
            className={`${styles.onboardingTab} ${mode === 'postpartum' ? styles.onboardingTabActive : ''}`}
            onClick={() => setMode('postpartum')}
          >
            {ui.onboarding.postpartumTab}
          </button>
        </div>

        {mode === 'pregnant' ? (
          <label className={styles.onboardingField}>
            <span>{ui.onboarding.dueDateLabel}</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={styles.onboardingInput}
            />
          </label>
        ) : (
          <label className={styles.onboardingField}>
            <span>{ui.onboarding.birthDateLabel}</span>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className={styles.onboardingInput}
            />
          </label>
        )}

        <div className={styles.onboardingActions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => {
              onRename(name);
              if (mode === 'pregnant' && dueDate) onSaveDueDate(dueDate);
              if (mode === 'postpartum' && birthDate) onSaveBirthDate(birthDate);
              onClose();
            }}
          >
            {ui.onboarding.save}
          </button>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => {
              onClear();
              onClose();
            }}
          >
            {ui.settings.clear}
          </button>
          <button type="button" className={styles.ghostButton} onClick={onClose}>
            {ui.settings.close}
          </button>
        </div>

        <div className={styles.restoreBox}>
          <p className={styles.restoreTitle}>{ui.settings.cloudTitle}</p>
          <p className={styles.restoreNote}>{ui.settings.cloudNote}</p>
          <div className={styles.shareActions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const result = await onCloudSave();
                setStatus(
                  result.ok && result.code
                    ? ui.settings.cloudSaved.replace('{code}', result.code)
                    : result.message
                );
                setBusy(false);
              }}
            >
              {ui.settings.cloudSave}
            </button>
          </div>
          <div className={styles.restoreRow}>
            <input
              type="text"
              value={restoreCode}
              onChange={(e) => setRestoreCode(e.target.value.toUpperCase())}
              className={styles.onboardingInput}
              placeholder="ABCD-1234"
            />
            <button
              type="button"
              className={styles.ghostButton}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const result = await onCloudRestore(restoreCode.trim());
                setStatus(result.message);
                setBusy(false);
              }}
            >
              {ui.settings.cloudRestore}
            </button>
          </div>
          {status ? (
            <p className={styles.shareStatus} role="status">
              {status}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
